import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, limit, getDocs, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';
import Button from '../../components/shared/UI/Button';
import CompanyDocuments from '../../components/company/CompanyDocuments';

// ── COMPANY TYPE RESOLUTION ──────────────────────────────────────────────────
// The canonical company-type keys. These mirror the values on the right of the
// sections' 'company_type:<type>' display_condition, so they are structural
// identifiers rather than display copy — option_text stays editable config.
//
// NOTE: this logic is intentionally duplicated in computeDSQScore
// (functions/index.js) so the scoring denominator branches the same way this
// form does. src/ and functions/ are separate npm packages — functions/ is
// self-contained and requires nothing from src/ — so there is no import path
// that would fit the project layout. The two copies must be changed together.
const COMPANY_TYPES = ['product', 'service', 'both'];

function normaliseTypeKey(value) {
  return String(value ?? '').trim().toLowerCase();
}

// Resolves one option to a canonical key: by option_id for a seeded selector,
// falling back to option_text so an admin-authored selector also works.
function optionTypeKey(option) {
  if (!option) return null;
  const byId = normaliseTypeKey(option.option_id);
  if (COMPANY_TYPES.includes(byId)) return byId;
  const byText = normaliseTypeKey(option.option_text);
  return COMPANY_TYPES.includes(byText) ? byText : null;
}

// The selector is the question whose options cover both 'product' and
// 'service'. Located from the template rather than a fixed section/question id,
// so an admin may place it in any shared section.
function findCompanyTypeQuestion(template) {
  for (const section of template?.sections || []) {
    for (const question of section.questions || []) {
      const keys = (question.options || []).map(optionTypeKey).filter(Boolean);
      if (keys.includes('product') && keys.includes('service')) {
        return { section, question };
      }
    }
  }
  return null;
}

export default function CompanyDSQPage() {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deal, setDeal] = useState(null);
  const [dealId, setDealId] = useState(null);
  const [template, setTemplate] = useState(null);
  const [, setExistingResponses] = useState({});
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [sectionAnswers, setSectionAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!user || !userProfile) return;
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userProfile]);

  async function loadData() {
    try {
      setLoading(true);
      const dId = userProfile?.deal_id;
      const fId = userProfile?.fund_id;

      if (!dId || !fId) {
        setError('Account configuration error. Please contact your Mirepa representative.');
        return;
      }
      setDealId(dId);

      // Fetch deal
      const dealSnap = await getDoc(doc(db, 'deals', dId));
      if (!dealSnap.exists()) {
        setError('Deal not found. Please contact your Mirepa representative.');
        return;
      }
      const dealData = dealSnap.data();
      setDeal(dealData);

      // Already submitted? The deal flag is written server-side by submitDSQ.
      if (dealData.dsq_company_submitted) {
        setSubmitted(true);
      }

      // Find the published DSQ template for this deal's fund + stream.
      // stream_id uses ?? null because Firestore's `== null` matches an
      // explicitly-null field only, never an absent one.
      const tmplQ = query(
        collection(db, 'ddq_templates'),
        where('fund_id', '==', fId),
        where('stream_id', '==', dealData.stream_id ?? null),
        where('template_type', '==', 'dsq'),
        where('status', '==', 'published'),
        limit(1)
      );
      const tmplSnap = await getDocs(tmplQ);
      if (tmplSnap.empty) {
        setError('No screening questionnaire template has been configured for your fund. Please contact your Mirepa representative.');
        return;
      }
      const tmplData = { id: tmplSnap.docs[0].id, ...tmplSnap.docs[0].data() };
      setTemplate(tmplData);

      // Fetch existing responses
      // visibility is constrained because the security rule for a company read
      // requires resource.data.visibility == 'shared'; Firestore denies a query
      // whose constraints do not match the rule.
      const responsesQ = query(
        collection(db, 'deals', dId, 'ddq_responses'),
        where('template_type', '==', 'dsq'),
        where('completed_by', '==', 'company'),
        where('visibility', '==', 'shared')
      );
      const responsesSnap = await getDocs(responsesQ);
      const existing = {};
      responsesSnap.docs.forEach(d => {
        existing[d.id] = d.data();
      });
      setExistingResponses(existing);

      // Pre-populate sectionAnswers from existing responses
      const prefilled = {};
      Object.entries(existing).forEach(([sectionId, data]) => {
        if (data.responses) {
          prefilled[sectionId] = {};
          data.responses.forEach(r => {
            prefilled[sectionId][r.question_id] = r;
          });
        }
      });
      setSectionAnswers(prefilled);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function getSharedSections() {
    if (!template) return [];
    return (template.sections || []).filter(s => s.visibility === 'shared');
  }

  // Returns 'product' | 'service' | 'both', or null when the type is not yet
  // resolvable — an unanswered selector, a template without one, or an answer
  // that maps to no canonical key. Null keeps every section visible, so a
  // company is never shown a blank form.
  function getCompanyType() {
    const found = findCompanyTypeQuestion(template);
    if (!found) return null;
    const answer = (sectionAnswers[found.section.section_id] || {})[found.question.question_id];
    if (!answer?.selected_option_id) return null;
    const option = (found.question.options || []).find(
      o => o.option_id === answer.selected_option_id
    );
    return optionTypeKey(option);
  }

  function shouldShowSection(section) {
    if (!section.display_condition) return true;
    const companyType = getCompanyType();
    if (!companyType) return true; // show all before type is answered
    const [, type] = section.display_condition.split(':');
    if (companyType === 'both') return true;
    return companyType === type;
  }

  function getVisibleSections() {
    return getSharedSections().filter(shouldShowSection);
  }

  function handleAnswerChange(questionId, value) {
    const section = getVisibleSections()[currentSectionIdx];
    if (!section) return;
    setSectionAnswers(prev => ({
      ...prev,
      [section.section_id]: {
        ...(prev[section.section_id] || {}),
        [questionId]: { question_id: questionId, ...value },
      },
    }));
  }

  async function handleSaveAndContinue() {
    const visibleSections = getVisibleSections();
    const section = visibleSections[currentSectionIdx];
    if (!section) return;

    setSaving(true);
    try {
      const responses = Object.values(sectionAnswers[section.section_id] || {});
      await setDoc(doc(db, 'deals', dealId, 'ddq_responses', section.section_id), {
        ddq_template_id: template.id,
        template_type: 'dsq',
        section_id: section.section_id,
        visibility: 'shared',
        responses,
        completed_by: 'company',
        saved_at: new Date().toISOString(),
        company_uid: user.uid,
      });

      if (currentSectionIdx < visibleSections.length - 1) {
        setCurrentSectionIdx(i => i + 1);
      }
    } catch (err) {
      alert('Failed to save. Please try again.\n' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    if (!window.confirm('Submit your screening questionnaire? You will not be able to edit your answers after submission.')) return;
    setSaving(true);
    try {
      // Save current section first
      const visibleSections = getVisibleSections();
      const section = visibleSections[currentSectionIdx];
      if (section) {
        const responses = Object.values(sectionAnswers[section.section_id] || {});
        await setDoc(doc(db, 'deals', dealId, 'ddq_responses', section.section_id), {
          ddq_template_id: template.id,
          template_type: 'dsq',
          section_id: section.section_id,
          visibility: 'shared',
          responses,
          completed_by: 'company',
          saved_at: new Date().toISOString(),
          company_uid: user.uid,
        });
      }

      // Submit server-side: the callable marks the responses submitted, flags
      // the deal and scores it. A company cannot write the deal directly.
      const fns = getFunctions();
      const submit = httpsCallable(fns, 'submitDSQ');
      await submit({ dealId });

      setSubmitted(true);
    } catch (err) {
      alert('Submission failed. Please try again.\n' + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="alert alert-error" style={{ marginTop: 20 }}>{error}</div>
    );
  }

  if (submitted) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Questionnaire Submitted</h2>
        <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', lineHeight: 1.7, maxWidth: 500, margin: '0 auto' }}>
          Thank you. Your screening questionnaire has been submitted.
          Your Mirepa Investment Advisors representative will be in touch.
        </p>
      </div>
    );
  }

  const visibleSections = getVisibleSections();
  const currentSection = visibleSections[currentSectionIdx];
  const isLastSection = currentSectionIdx === visibleSections.length - 1;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
          {deal?.company_name} — Deal Screening Questionnaire
        </h1>
        {deal?.deal_reference && (
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Ref: {deal.deal_reference}</p>
        )}
      </div>

      {/* Progress indicator */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
        {visibleSections.map((s, i) => (
          <div key={s.section_id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => i <= currentSectionIdx && setCurrentSectionIdx(i)}
              style={{
                width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: i <= currentSectionIdx ? 'pointer' : 'default',
                background: i === currentSectionIdx ? '#1e3a5f' : i < currentSectionIdx ? 'var(--color-success)' : 'var(--color-border)',
                color: i <= currentSectionIdx ? '#fff' : 'var(--color-text-secondary)',
                fontSize: 13, fontWeight: 600,
              }}
            >
              {i < currentSectionIdx ? '✓' : i + 1}
            </button>
            {i < visibleSections.length - 1 && (
              <div style={{ height: 2, width: 24, background: i < currentSectionIdx ? 'var(--color-success)' : 'var(--color-border)' }} />
            )}
          </div>
        ))}
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
          Section {currentSectionIdx + 1} of {visibleSections.length}
        </span>
      </div>

      {currentSection && (
        <div className="card">
          <div className="card-header">
            <h2>{currentSection.title}</h2>
            {currentSection.description && (
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                {currentSection.description}
              </p>
            )}
          </div>
          <div className="card-body">
            {(currentSection.questions || []).length === 0 ? (
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                No questions have been added to this section yet. Click "{isLastSection ? 'Submit' : 'Save and Continue'}" to proceed.
              </p>
            ) : (
              (currentSection.questions || []).map(question => (
                <CompanyQuestion
                  key={question.question_id}
                  question={question}
                  answer={sectionAnswers[currentSection.section_id]?.[question.question_id]}
                  onChange={handleAnswerChange}
                  disabled={submitted}
                />
              ))
            )}
          </div>
          <div className="card-footer" style={{ justifyContent: 'flex-end', gap: 12 }}>
            {currentSectionIdx > 0 && (
              <Button variant="secondary" onClick={() => setCurrentSectionIdx(i => i - 1)}>
                ← Previous
              </Button>
            )}
            {isLastSection ? (
              <Button variant="primary" loading={saving} onClick={handleSubmit}>
                Submit DSQ
              </Button>
            ) : (
              <Button variant="primary" loading={saving} onClick={handleSaveAndContinue}>
                Save and Continue →
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Dataroom-level document upload (docs/29:163-165). Additive section,
          separate from the question renderer — deliberately NOT a per-question
          attachment. Uploads go to the Drive Company Documents/ folder via the
          authorised uploadDealDocument callable. */}
      {dealId && <CompanyDocuments dealId={dealId} />}
    </div>
  );
}

function CompanyQuestion({ question, answer, onChange, disabled }) {
  const { question_id, question_text, response_type, options, figure_unit } = question;

  function handleChange(value) {
    onChange(question_id, value);
  }

  return (
    <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--color-border-light)' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{question_text}</p>

      {(response_type === 'binary' || response_type === 'multiple_choice') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(options || []).map(opt => (
            <label key={opt.option_id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: disabled ? 'default' : 'pointer' }}>
              <input
                type={response_type === 'binary' ? 'radio' : 'radio'}
                name={question_id}
                value={opt.option_id}
                checked={answer?.selected_option_id === opt.option_id}
                onChange={() => handleChange({ selected_option_id: opt.option_id, response_value: null })}
                disabled={disabled}
              />
              <span style={{ fontSize: 14 }}>{opt.option_text}</span>
            </label>
          ))}
        </div>
      )}

      {response_type === 'figure' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="text"
            className="form-input"
            style={{ maxWidth: 200 }}
            value={answer?.response_value || ''}
            onChange={e => handleChange({ response_value: e.target.value, selected_option_id: null })}
            disabled={disabled}
          />
          {figure_unit && <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>{figure_unit}</span>}
        </div>
      )}

      {response_type === 'text' && (
        <input
          type="text"
          className="form-input"
          value={answer?.response_value || ''}
          onChange={e => handleChange({ response_value: e.target.value, selected_option_id: null })}
          disabled={disabled}
        />
      )}

      {response_type === 'long_text' && (
        <textarea
          className="form-textarea"
          rows={4}
          style={{ resize: 'vertical', fontFamily: 'inherit' }}
          value={answer?.response_value || ''}
          onChange={e => handleChange({ response_value: e.target.value, selected_option_id: null })}
          disabled={disabled}
        />
      )}

      {response_type === 'date' && (
        <input
          type="date"
          className="form-input"
          style={{ maxWidth: 200 }}
          value={answer?.response_value || ''}
          onChange={e => handleChange({ response_value: e.target.value, selected_option_id: null })}
          disabled={disabled}
        />
      )}
    </div>
  );
}
